(() => {
  if (window.cpMegaMenuInitialized) return;
  window.cpMegaMenuInitialized = true;

  const desktop = window.matchMedia('(min-width: 990px)');
  const closeTimers = new WeakMap();
  let positionFrame;
  let resizeTimer;

  const getPanel = (details) => {
    const summary = details?.querySelector(':scope > summary');
    const panelId = summary?.getAttribute('aria-controls');
    return panelId ? document.getElementById(panelId) : null;
  };

  const getOwner = (panel) => {
    const ownerId = panel?.dataset.cpMegaOwner;
    return ownerId ? document.getElementById(ownerId) : null;
  };

  const portalPanel = (details) => {
    if (!details) return null;

    let panel = getPanel(details);
    if (!panel) panel = details.querySelector(':scope > .cp-mega-menu__content');
    if (!panel) return null;

    const summary = details.querySelector(':scope > summary');
    if (summary && !summary.getAttribute('aria-controls')) summary.setAttribute('aria-controls', panel.id);

    panel.dataset.cpMegaOwner = details.id;
    panel.setAttribute('aria-hidden', details.open && desktop.matches ? 'false' : 'true');

    const headerWrapper = details.closest('.header-wrapper');
    if (headerWrapper && panel.parentElement !== headerWrapper) headerWrapper.appendChild(panel);

    return panel;
  };

  const positionPanel = (details) => {
    if (!desktop.matches || !details?.open) return;

    const panel = getPanel(details);
    const header = details.closest('.header-wrapper')?.querySelector(':scope > .header');
    const logo = header?.querySelector('.header__heading, .header__heading-link');
    const nav = header?.querySelector('.header__inline-menu');
    if (!panel || !header || !logo || !nav) return;

    const headerRect = header.getBoundingClientRect();
    const logoRect = logo.getBoundingClientRect();
    const navRect = nav.getBoundingClientRect();
    const gap = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--cp-mega-gap')) || 0;

    const viewportPadding = 20;
    const left = Math.max(viewportPadding, logoRect.left);
    const right = Math.min(window.innerWidth - viewportPadding, navRect.right);
    const width = Math.max(280, right - left);
    const top = headerRect.bottom + gap;

    panel.style.setProperty('position', 'fixed', 'important');
    panel.style.setProperty('top', `${top}px`, 'important');
    panel.style.setProperty('left', `${left}px`, 'important');
    panel.style.setProperty('right', 'auto', 'important');
    panel.style.setProperty('width', `${width}px`, 'important');
    panel.style.setProperty('max-width', `${width}px`, 'important');
    panel.style.setProperty('z-index', '60', 'important');
  };

  const queuePositionOpenPanels = () => {
    if (!desktop.matches) return;
    if (positionFrame) cancelAnimationFrame(positionFrame);
    positionFrame = requestAnimationFrame(() => {
      positionFrame = null;
      document.querySelectorAll('.section-header details.cp-mega-menu[open]').forEach(positionPanel);
    });
  };

  const showPanel = (details) => {
    if (!desktop.matches) return;
    const panel = getPanel(details);
    const summary = details?.querySelector(':scope > summary');
    if (!panel || !summary) return;

    positionPanel(details);
    summary.setAttribute('aria-expanded', 'true');
    panel.setAttribute('aria-hidden', 'false');
    panel.style.setProperty('opacity', '1', 'important');
    panel.style.setProperty('visibility', 'visible', 'important');
    panel.style.setProperty('pointer-events', 'auto', 'important');
    panel.style.setProperty('transform', 'translateY(0)', 'important');
  };

  const hidePanel = (details) => {
    const panel = getPanel(details);
    const summary = details?.querySelector(':scope > summary');
    if (!panel || !summary) return;

    summary.setAttribute('aria-expanded', 'false');
    panel.setAttribute('aria-hidden', 'true');
    panel.style.setProperty('opacity', '0', 'important');
    panel.style.setProperty('visibility', 'hidden', 'important');
    panel.style.setProperty('pointer-events', 'none', 'important');
    panel.style.setProperty('transform', 'translateY(-.8rem)', 'important');
  };

  const cancelClose = (details) => {
    const timer = closeTimers.get(details);
    if (timer) {
      clearTimeout(timer);
      closeTimers.delete(details);
    }
  };

  const closeMenu = (details, focusTrigger = false) => {
    if (!details) return;
    cancelClose(details);
    details.removeAttribute('open');
    hidePanel(details);
    if (focusTrigger) details.querySelector(':scope > summary')?.focus();
  };

  const closeOtherMenus = (current) => {
    document.querySelectorAll('.section-header details.cp-mega-menu[open]').forEach((details) => {
      if (details !== current) closeMenu(details);
    });
  };

  const openMenu = (details) => {
    if (!desktop.matches || !details) return;
    cancelClose(details);
    closeOtherMenus(details);
    details.setAttribute('open', '');
    showPanel(details);
  };

  const scheduleClose = (details, delay = 180) => {
    cancelClose(details);
    const timer = setTimeout(() => {
      const panel = getPanel(details);
      const triggerHovered = details.matches(':hover');
      const panelHovered = panel?.matches(':hover');
      const focusInTrigger = details.contains(document.activeElement);
      const focusInPanel = panel?.contains(document.activeElement);

      if (!triggerHovered && !panelHovered && !focusInTrigger && !focusInPanel) closeMenu(details);
    }, delay);
    closeTimers.set(details, timer);
  };

  const bindPanel = (panel) => {
    if (!panel || panel.dataset.cpMegaPanelReady === 'true') return;
    panel.dataset.cpMegaPanelReady = 'true';

    panel.addEventListener('mouseenter', () => {
      const owner = getOwner(panel);
      if (!desktop.matches || !owner) return;
      cancelClose(owner);
      if (!owner.open) owner.setAttribute('open', '');
      showPanel(owner);
    });

    panel.addEventListener('mouseleave', () => {
      const owner = getOwner(panel);
      if (!desktop.matches || !owner) return;
      scheduleClose(owner);
    });

    panel.addEventListener('focusin', () => {
      const owner = getOwner(panel);
      if (!desktop.matches || !owner) return;
      cancelClose(owner);
      if (!owner.open) owner.setAttribute('open', '');
      showPanel(owner);
    });

    panel.addEventListener('focusout', () => {
      const owner = getOwner(panel);
      if (!desktop.matches || !owner) return;
      setTimeout(() => scheduleClose(owner, 40), 0);
    });

    panel.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      const owner = getOwner(panel);
      if (!owner) return;
      event.preventDefault();
      closeMenu(owner, true);
    });
  };

  const bindTrigger = (details) => {
    if (!details || details.dataset.cpMegaHoverReady === 'true') return;
    details.dataset.cpMegaHoverReady = 'true';

    const summary = details.querySelector(':scope > summary');
    const panel = portalPanel(details);
    if (!summary || !panel) return;
    bindPanel(panel);

    summary.setAttribute('aria-expanded', details.open && desktop.matches ? 'true' : 'false');
    if (!desktop.matches || !details.open) hidePanel(details);

    details.addEventListener('mouseenter', () => openMenu(details));
    details.addEventListener('mouseleave', () => scheduleClose(details));

    details.addEventListener('focusin', () => {
      if (!desktop.matches) return;
      cancelClose(details);
      openMenu(details);
    });

    details.addEventListener('focusout', () => {
      if (!desktop.matches) return;
      setTimeout(() => scheduleClose(details, 40), 0);
    });

    details.addEventListener('toggle', () => {
      if (!desktop.matches) {
        hidePanel(details);
        return;
      }
      if (details.open) {
        cancelClose(details);
        showPanel(details);
      } else {
        cancelClose(details);
        hidePanel(details);
      }
    });

    summary.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      closeMenu(details, true);
    });
  };

  const enhanceMegaMenus = (root = document) => {
    const scope = root.querySelectorAll ? root : document;
    scope.querySelectorAll('.section-header details.cp-mega-menu').forEach(bindTrigger);
    queuePositionOpenPanels();
  };

  const closeAll = () => {
    document.querySelectorAll('.section-header details.cp-mega-menu').forEach((details) => {
      if (details.open) details.removeAttribute('open');
      hidePanel(details);
    });
  };

  const init = () => enhanceMegaMenus(document);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();

  window.addEventListener('scroll', queuePositionOpenPanels, { passive: true });
  window.addEventListener('resize', () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(queuePositionOpenPanels, 80);
  });

  desktop.addEventListener('change', () => {
    if (!desktop.matches) closeAll();
    else enhanceMegaMenus(document);
  });

  document.addEventListener('pointerdown', (event) => {
    if (!desktop.matches) return;
    document.querySelectorAll('.section-header details.cp-mega-menu[open]').forEach((details) => {
      const panel = getPanel(details);
      if (details.contains(event.target) || panel?.contains(event.target)) return;
      closeMenu(details);
    });
  });

  document.addEventListener('shopify:section:load', (event) => enhanceMegaMenus(event.target));
})();
